import { cn } from "@-/com.npmjs.cn";
import { BlurView as BlurViewFromExpoBlur } from "@-/com.npmjs.expo-blur";
import { Platform, View, type ViewProps } from "@-/com.npmjs.react-native";
import { useUniwind, withUniwind } from "@-/com.npmjs.uniwind";
import { useContext } from "react";
import { CapturedBackdropContext, FrostedBackdropContext } from "#frosted-backdrop-contexts";

const BlurView = withUniwind(BlurViewFromExpoBlur);

/** Ordinary frosted material; no distortion, refraction, or simulated Liquid Glass highlights. */
export function FrostedSurface({
  children,
  className,
  intensity = 80,
  material = "regular",
  selected = false,
  ...props
}: ViewProps & {
  intensity?: number;
  material?: "regular" | "thick";
  selected?: boolean;
}) {
  const { theme } = useUniwind();
  const target = useContext(FrostedBackdropContext)?.target ?? null;
  const captured = useContext(CapturedBackdropContext);
  // Dimezis forbids a target containing a BlurView that captures the same target.
  const canBlur = Platform.OS !== "android" || (target !== null && !captured.includes(target));
  const dark = theme === "dark";
  return (
    <View
      {...props}
      className={cn(
        "overflow-hidden",
        !canBlur && (selected ? "bg-accent" : "bg-background"),
        className,
      )}
    >
      {canBlur && (
        <BlurView
          accessibilityElementsHidden
          accessible={false}
          blurMethod="dimezisBlurViewSdk31Plus"
          blurTarget={target ?? undefined}
          className="absolute inset-0"
          importantForAccessibility="no-hide-descendants"
          intensity={intensity}
          pointerEvents="none"
          tint={
            material === "thick"
              ? dark
                ? "systemThickMaterialDark"
                : "systemThickMaterialLight"
              : dark
                ? "systemMaterialDark"
                : "systemMaterialLight"
          }
        />
      )}
      {selected && canBlur ? (
        <View className="absolute inset-0 bg-muted/50" pointerEvents="none" />
      ) : null}
      {children}
    </View>
  );
}
